import { createFileRoute } from "@tanstack/react-router";
import {
  FileText, Camera, Ruler, ShieldCheck, Handshake, ArrowUpRight,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { LeadForm } from "@/components/site/LeadForm";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import venderCasa from "@/assets/vender-casa.webp";
import venderCasaSrcSet from "@/assets/vender-casa.webp?w=480;768;1200&format=webp&as=srcset";

export const Route = createFileRoute("/vender-a-minha-casa")({
  component: VenderMinhaCasa,
  head: () => ({
    meta: [
      { title: "Vender a Minha Casa — InvestCapital" },
      { name: "description", content: "Quer vender o seu imóvel com rapidez e a pronto pagamento? Veja a documentação, fotografias e informações necessárias para darmos início ao processo." },
      { property: "og:title", content: "Vender a Minha Casa — InvestCapital" },
      { property: "og:description", content: "Compramos a sua casa com capitais próprios. Sem intermediários, sem comissões e sem dependência bancária." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:url", content: "https://www.investcapital.pt/vender-a-minha-casa" },
    ],
    links: [{ rel: "canonical", href: "https://www.investcapital.pt/vender-a-minha-casa" }],
  }),

});

const sections = [
  {
    icon: FileText,
    title: "Documentação Essencial do Imóvel",
    intro:
      "Para podermos fazer uma análise financeira e jurídica rápida e apresentar uma proposta firme, o primeiro passo é a verificação documental:",
    items: [
      ["Caderneta Predial Urbana (CPU) atualizada", "Documento das Finanças que identifica a matriz, a localização e a área do imóvel."],
      ["Certidão do Registo Predial (CRP)", "Para confirmar a titularidade do imóvel e verificar a ausência de ónus ou encargos."],
      ["Documento de identificação (Cartão de Cidadão / Passaporte)", "Dos proprietários legítimos do imóvel."],
      ["Licença de utilização e certificado energético", "Caso já os tenha disponíveis (essenciais para a formalização da escritura)."],
    ],
  },
  {
    icon: Camera,
    title: "Fotografias e Vídeos Nítidos do Imóvel",
    intro:
      "Não precisa de fotos profissionais, mas imagens bem iluminadas e claras ajudam-nos a avaliar o estado real do imóvel e a acelerar a resposta:",
    items: [
      ["Fotografe com boa luz natural", "Abra as janelas e persianas durante o dia."],
      ["Apanhe as divisões principais", "Fotos da sala, quartos, cozinha, casas de banho, varandas/terraços e fachada exterior do edifício."],
      ["Mostre o estado atual", "Fotos nítidas do estado de conservação — mesmo que precise de obras de remodelação total ou parcial, compramos o imóvel no estado em que se encontra."],
    ],
  },
  {
    icon: Ruler,
    title: "Informações Técnicas e Características do Imóvel",
    intro:
      "Quanto mais detalhes nos fornecer sobre as características da casa, mais precisa e ajustada será a nossa avaliação inicial:",
    items: [
      ["Áreas e tipologia", "Tipologia (T1, T2, T3…), área útil e área bruta."],
      ["Localização exata", "Morada completa ou código postal para podermos analisar a envolvente e o mercado local em Portugal continental."],
      ["Atributos adicionais", "Indique se inclui garagem, arrecadação, elevador, terraço ou vista desimpedida."],
      ["Situação atual do imóvel", "Indique se a casa se encontra devoluta (vazia), habitada pelos proprietários ou arrendada a terceiros."],
    ],
  },
  {
    icon: ShieldCheck,
    title: "Transparência no Estado de Conservação e Situação do Imóvel",
    intro:
      "Compramos imóveis em qualquer estado de conservação, por isso a honestidade e a clareza poupam tempo a ambas as partes:",
    items: [
      ["Imóveis a precisar de obras", "Se a casa precisar de remodelação profunda, intervenção estrutural ou atualização de canalização/eletricidade, informe-nos abertamente."],
      ["Situações especiais ou urgentes", "Se o imóvel estiver associado a processos de herança, partilhas ou necessidade de liquidez imediata, temos experiência para lidar com estes processos de forma rápida e discreta."],
    ],
  },
  {
    icon: Handshake,
    title: "Processo Simples: Da Proposta à Escritura a Pronto",
    intro:
      "Explicamos exatamente como funciona o nosso modelo de atuação para que saiba com o que contar:",
    items: [
      ["Sem intermediários nem comissões", "Ao vender diretamente à InvestCapital, não paga comissões imobiliárias."],
      ["Sem dependência de aprovação bancária", "Como utilizamos capitais próprios, não estamos sujeitos a prazos ou aprovações de crédito habitação."],
      ["Escritura rápida", "Assim que a documentação é validada, agendamos a escritura e realizamos o pagamento na totalidade."],
    ],
  },
];

function VenderMinhaCasa() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>

      <section className="border-b border-border/60 bg-primary-soft/40">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-2 md:items-center md:py-20">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Vender a minha casa</p>
            <h1 className="mt-2 font-display text-3xl font-semibold leading-tight md:text-5xl">
              Quer vender o seu imóvel com rapidez e a pronto pagamento?
            </h1>
            <p className="mt-4 max-w-xl text-lg text-muted-foreground">
              Compramos a sua casa com capitais próprios. Veja o que precisa para darmos início ao processo.
            </p>
            <div className="mt-8">
              <a
                href="#vender-imovel"
                className="inline-flex items-center gap-2 rounded-full bg-[#22c55e] px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition-transform hover:scale-105 hover:bg-[#16a34a]"
              >
                Vender imóvel
                <ArrowUpRight className="h-5 w-5" strokeWidth={2.5} />
              </a>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src={venderCasa}
              srcSet={venderCasaSrcSet}
              sizes="(max-width: 768px) 100vw, 50vw"
              alt="Documentos do imóvel e chaves de casa sobre uma mesa numa sala luminosa"
              className="h-full w-full object-cover"
              width={1600}
              height={1008}
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <div className="space-y-10">
          {sections.map((s, i) => (
            <article key={s.title} className="rounded-2xl border border-border bg-card p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-muted-foreground">0{i + 1}</div>
                  <h2 className="font-display text-xl font-semibold md:text-2xl">{s.title}</h2>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">{s.intro}</p>
              <ul className="mt-5 space-y-4">
                {s.items.map(([label, body]) => (
                  <li key={label} className="border-l-2 border-primary/40 pl-4">
                    <p className="text-sm font-semibold text-foreground">{label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{body}</p>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section id="vender-imovel" className="scroll-mt-24 bg-primary-soft/60 py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-semibold md:text-4xl">Peça a sua proposta</h2>
            <p className="mt-3 text-muted-foreground">
              Envie-nos os dados do imóvel. Em 48h úteis entramos em contacto com uma
              proposta de compra a pronto pagamento — sem burocracias, sem intermediários
              e sem comissões.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <LeadForm />
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
      <FloatingCTA />
    </div>
  );
}
